<template>
  <BaseDialog
    :title="$t('member.addSignatureDialog.title')"
    width="600px"
    :btn1Loading="loading"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <BobForm
      :fields="fields"
      :rules="formRules"
      :model="formData"
      :formRef.sync="formRef"
      label-position="top"
    />
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, provide } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BobForm from '@/components/BobForm.vue'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import { minRules, noEmptyRules } from '@/validation'
import { GetSignatureTemplate } from '@/api/signatureTemplate'
import { CreateSignatureRecordMapping } from '@/api/signatureRecordMapping'
import FormItemTooltip from '@/components/FormItem/FormItemTooltip.vue'
import BaseSearch from '@/components/Search/BaseSearch.vue'

export default defineComponent({
  name: 'AddSignatureDialog',
  components: {
    BaseDialog,
    BobForm,
  },
  props: {
    memberId: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formRef, formData, initFormData, formSubmit, loading } = useBaseForm()

    initFormData({
      signatureTemplateId: '',
      signDeadlineDays: '',
    })

    provide('formData', formData)

    const formRules = {
      signatureTemplateId: [noEmptyRules()],
      signDeadlineDays: [
        noEmptyRules(),
        minRules(),
      ],
    }

    const fields = computed(() => [
      {
        prop: 'signatureTemplateId',
        label: t('member.addSignatureDialog.agreementName'),
        component: FormItemTooltip,
        props: {
          tooltip: t('member.addSignatureDialog.agreementTooltip'),
        },
        body: {
          component: BaseSearch,
          props: {
            value: formData.signatureTemplateId,
            api: GetSignatureTemplate,
            filter: {
              sourceType: 'manual',
            },
            objKey: 'id',
            placeholder: t('member.addSignatureDialog.pleaseSelectAgreement'),
            class: 'w-full',
          },
          on: {
            'update:value': (val) => {
              formData.signatureTemplateId = val
            },
          },
        },
      },
      {
        label: t('member.addSignatureDialog.linkValidDays'),
        component: FormItemTooltip,
        props: {
          tooltip: t('member.addSignatureDialog.linkValidTooltip'),
          prop: 'signDeadlineDays',
        },
        body: {
          component: 'div',
          props: {
            class: 'w-full flex gap-2',
          },
          slots: {
            default: [
              {
                component: 'BaseElInput',
                props: {
                  type: 'number',
                  min: 0,
                  placeholder: t('member.addSignatureDialog.pleaseEnterDays'),
                  value: formData.signDeadlineDays,
                  class: 'flex-1',
                },
                on: {
                  input: (val) => {
                    formData.signDeadlineDays = val
                  },
                },
              },
              {
                component: 'span',
                render: () => t('member.addSignatureDialog.daysHint'),
              },
            ],
          },
        },
      },
    ])

    const onConfirm = () => {
      formSubmit(async () => {
        const payload = {
          shopId: shopId.value,
          SignatureTemplateId: formData.signatureTemplateId,
          MemberId: props.memberId,
          signDeadlineDays: Number(formData.signDeadlineDays),
        }
        const [, errMsg] = await CreateSignatureRecordMapping(payload)

        if (errMsg) {
          window.$message.error(errMsg)
          return
        }

        window.$message.success(t('member.addSignatureDialog.createSuccess'))
        emit('refresh')
        emit('close')
      })
    }

    return {
      loading,
      formRef,
      formData,
      formRules,
      fields,
      onConfirm,
    }
  },
})
</script>

<style scoped lang="postcss">
:deep(.el-select .el-input) {
  @apply w-full;
}
</style>
